import { UserAgentInfo } from 'dg-web-shared/lib/UserAgentInfo';
import { Typo } from 'dg-web-shared/ui/typo.ts';
import { css } from '@emotion/css';
import { Colors } from 'dg-web-shared/ui/vars';
import { paper } from 'dg-web-shared/tb-ui/paper.ts';
import React, { useRef, useState, type JSX, useLayoutEffect } from 'react';
import { Icon as Icon1 } from 'dg-web-shared/ui/icons/Icon';
import { Clickable } from 'dg-web-shared/ui/Clickable';

export function getSelectedDisplayText<I>(
    options: Option<I>[],
    selection: I,
): string {
    if (selection === undefined) {
        return '';
    }
    const index = options.map((o: Option<I>) => o.id).indexOf(selection);

    return options[index].displayText;
}

export interface Option<I> {
    id: I;
    displayText: string;
}

interface OptionsMenuProps<I> {
    selection: I;
    onChange: ChangeHandler<I>;
    options: Option<I>[];
    open: boolean;
    compatibility: boolean;
}

function OptionsMenu<I>(props: OptionsMenuProps<I>) {
    const selectedOptionRef = useRef<HTMLDivElement>(null);
    const optionsContainerRef = useRef<HTMLDivElement>(null);

    function handleOnClick(option: Option<I>): void {
        props.onChange(option.id);
    }

    function renderOptions(): React.ReactNode {
        return props.options.map((option: Option<I>) => {
            const optionIsSelected = props.selection === option.id;
            return (
                <Clickable
                    element="div"
                    onClick={(): void => handleOnClick(option)}
                    key={option.id as string}
                >
                    <div
                        className={css({
                            ...Typo.navbutton,
                            color: Colors.blue,
                            paddingTop: '10px',
                            paddingBottom: '11px',
                            paddingLeft: '16px',
                            paddingRight: '16px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        })}
                        ref={optionIsSelected ? selectedOptionRef : undefined}
                    >
                        {option.displayText}
                    </div>
                </Clickable>
            );
        });
    }

    function renderMenu(): React.ReactNode {
        if (!props.open) {
            return null;
        }
        return (
            <div
                className={css({
                    position: 'relative',
                    top: 0,
                    left: 0,
                    right: 0,
                    maxHeight: '225px',
                    ...paper(1),
                    opacity: 1,
                    overflow: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    background: Colors.white,
                    transformOrigin: 'right 15px',
                })}
                key="optionsMenu"
                ref={optionsContainerRef}
            >
                <div key="optionsContainer">{renderOptions()}</div>
            </div>
        );
    }

    function updateScrollPosition(): void {
        if (props.open) {
            const selectedOption = selectedOptionRef.current;
            const optionsContainer = optionsContainerRef.current;

            // 25 is a compensation value s.t. the item is not aligned exatly to the top of the
            // container. the value depends on margin and padding of a list item
            if (optionsContainer !== null && selectedOption !== null) {
                optionsContainer.scrollTop = selectedOption.offsetTop - 25;
            }
        }
    }

    // componentDidMount + componentDidUpdate both called updateScrollPosition,
    // which together is an effect with no dependency array.
    useLayoutEffect(() => {
        updateScrollPosition();
    });

    return (
        <div
            className={css({
                position: props.compatibility ? 'relative' : 'absolute',
                top: '-10px',
                right: '4px',
                transformOrigin: 'right 15px',
                zIndex: 20,
                WebkitOverflowScrolling: 'touch',
            })}
        >
            {renderMenu()}
        </div>
    );
}

const OptionMenuFactory = <I,>(
    props: OptionsMenuProps<I>,
    ...children: React.ReactNode[]
): React.ReactElement =>
    React.createElement(OptionsMenu<I>, props, ...children);

export interface SelectionIconProps {
    singleSelection: boolean;
}

function SelectionIcon(props: SelectionIconProps) {
    if (props.singleSelection) {
        return null;
    } else {
        return React.createElement(
            'div',
            {
                className: css({
                    color: Colors.red,
                    position: 'absolute',
                    top: '-50%',
                    right: 0,
                }),
            } as object,
            Icon1({ icon: 'arrowDropdown' }),
        );
    }
}

const SelectionIconFactory = (props: SelectionIconProps): React.ReactElement =>
    React.createElement(SelectionIcon, props);

interface SelectionComponentProps {
    selectionText: string;
    singleSelection: boolean;
    key?: string;
}

function Selection(props: SelectionComponentProps) {
    return React.createElement(
        'div',
        {
            className: css({
                position: 'relative',
                marginTop: '12px',
                ...Typo.navbutton,
            }),
            'data-single-selection': props.singleSelection,
        } as object,
        React.createElement(
            'div',
            {
                className: css({
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    marginRight: '24px',
                    color: Colors.blue,
                }),
            } as object,
            props.selectionText,
        ),
        SelectionIconFactory({
            singleSelection: props.singleSelection,
        }),
    );
}

export interface ChangeHandler<I> {
    (selection: I): void;
}

export interface OptionsGenerator<O, I> {
    (e: O, i?: number): Option<I>;
}

export interface Props<O, I> {
    errorText?: string;
    labelText?: string;
    hintText?: string;
    compatibility?: boolean;
    onChange: ChangeHandler<I>;
    optionsGenerator: OptionsGenerator<O, I>;
    selection: I;
    data: O[];
    key?: string;
}

function MouseTrapFactory(
    open: boolean,
    closeCb: () => void,
): JSX.Element | null {
    if (open) {
        return (
            <Clickable
                element="div"
                className={css({
                    position: 'fixed',
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    zIndex: 10,
                })}
                onClick={closeCb}
            />
        );
    }
    return null;
}

// `static defaultProps` is ignored on function components in React 19, so the
// defaults move into the destructuring below. These two are module-level so they
// keep their old identity: the class evaluated the defaults object exactly once,
// and a fresh `[]` per render would break referential equality for consumers.
const DEFAULT_COMPATIBILITY = new UserAgentInfo(null)
    .isProblematicAndroidStockBrowser;
const DEFAULT_DATA: never[] = [];

export function GenericDropdown<O, I>({
    errorText = '',
    labelText = '',
    hintText = '',
    compatibility = DEFAULT_COMPATIBILITY,
    data = DEFAULT_DATA,
    ...rest
}: Props<O, I>) {
    const props: Props<O, I> = {
        errorText,
        labelText,
        hintText,
        compatibility,
        data,
        ...rest,
    };
    const [open, setOpen] = useState(false);

    function _handleOnClick(): void {
        if (!isSingleSelection()) {
            setOpen(!open);
        }
    }

    function _handleChange(o: I): void {
        close();
        props.onChange(o);
    }

    function close(): void {
        setOpen(false);
    }

    function renderClosedDropdown(options: Option<I>[]): React.ReactNode {
        const p = props;
        if (open && p.compatibility) {
            return null;
        } else {
            return [
                <Selection
                    singleSelection={isSingleSelection()}
                    selectionText={getSelectedDisplayText<I>(
                        options,
                        p.selection,
                    )}
                    key="SelectionComponent"
                />,
            ];
        }
    }

    const p = props;
    const options = p.optionsGenerator ? p.data.map(p.optionsGenerator) : [];
    return (
        <Clickable
            element="div"
            className={css({
                marginTop: 0,
                display: 'block',
                position: 'relative',
            })}
            onClick={(): void => _handleOnClick()}
        >
            {renderClosedDropdown(options)}
            {MouseTrapFactory(open, (): void => close())}
            {
                /* need of factory because jsx and generic don't work */
                OptionMenuFactory({
                    selection: p.selection,
                    open: open,
                    onChange: ((o: I): void =>
                        _handleChange(o)) as ChangeHandler<unknown>,
                    options: options,
                    compatibility: props.compatibility || false,
                })
            }
        </Clickable>
    );

    function isSingleSelection(): boolean {
        return !(props.data.length > 1);
    }
}

export function StringIdFactory<O>(
    props: Props<O, string>,
): React.ReactElement<Props<O, string>> {
    return React.createElement<Props<O, string>>(GenericDropdown, props);
}
